<template>
  <div class="full-height">
    <spinner v-if="loading" />

    <div v-else-if="localConfig && localConfig.campaignData" class="full-height">
      <card>
        <!-- Name, type -->
        <div class="row">
          <!-- Name -->
          <div class="slds-form-element col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.name')"
                :validation="['required']"
                ref="data_name"
                v-if="isCreate || isEdit"
                v-model="name" />

              <output-field
                :label="$t('campaignDesigner.campaignSetup.name')"
                :value="name"
                v-else />
            </div>
          </div>

          <!-- Campaign type selection -->
          <div class="slds-form-element slds-size--medium col">
            <campaign-type-selector
              ref="data_campaignType"
              v-if="isCreate && localConfig.campaignData.type.options.length > 1"
              v-model="campaignType"
              :options="localConfig.campaignData.type.options"
              :validation="['required']" />

            <output-field :label="$t('campaignDesigner.campaignSetup.type.label')" type="custom" v-else>
              <span class="slds-swatch type-color-container" :style="`background:${campaignType.color ? campaignType.color: '#FFFFFF'};`" />
              <span>{{campaignType.label}}</span>
            </output-field>
          </div>
        </div>

        <!-- Target Source, Market -->
        <div class="row">
          <!-- Market -->
          <div class="slds-form-element slds-size--medium col">
            <picklist
              can-remove-elements
              :label="$t('campaignDesigner.campaignSetup.market')"
              :options="localConfig.campaignData.markets.options"
              :validation="['required']"
              labels-prefix="markets"
              ref="data_markets"
              v-if="isCreate || isEdit"
              v-model="markets" />

            <output-field
              :label="$t('campaignDesigner.campaignSetup.market')"
              :options="localConfig.campaignData.markets.options"
              :value="markets"
              labels-prefix="markets"
              type="picklist"
              v-else />
          </div>
        </div>

        <!-- Target Source, Deduplication parameter -->
        <div class="row">
          <!-- Target Source -->
          <div class="slds-form-element slds-size--medium col">
            <picklist
              :label="$t('campaignDesigner.campaignSetup.targetSource.label')"
              :options="localConfig.campaignData.targetSource.options"
              :validation="['required']"
              labels-prefix="campaignDesigner.campaignSetup.targetSource"
              show-empty-value
              ref="data_targetSource"
              v-if="isCreate || isEdit"
              v-model="targetSource" />

            <output-field
              :label="$t('campaignDesigner.campaignSetup.targetSource.label')"
              :options="localConfig.campaignData.targetSource.options"
              :value="targetSource"
              labels-prefix="campaignDesigner.campaignSetup.targetSource"
              type="picklist"
              v-else />
          </div>

          <!-- Deduplication parameter -->
          <div class="slds-form-element slds-size--medium col">
            <template v-if="!targetSource">
              <picklist
                :label="$t('campaignDesigner.targetDefinition.deduplication.label')"
                :options="localConfig.campaignData.deduplicationParameter.options"
                :validation="['required']"
                disabled
                show-empty-value
                v-if="isCreate || isEdit"
                v-model="deduplicationParameter" />

              <span v-else>{{nbsp}}</span>
            </template>
            <template v-else-if="targetSource === 'contacts'">
              <output-field
                labels-prefix="campaignDesigner.targetDefinition.deduplication"
                type="picklist"
                :label="$t('campaignDesigner.targetDefinition.deduplication.label')"
                :options="localConfig.campaignData.deduplicationParameter.options"
                :value="deduplicationParameter" />
            </template>
            <template v-else-if="targetSource === 'contacts_leads'">
              <picklist
                :label="$t('campaignDesigner.targetDefinition.deduplication.label')"
                :options="localConfig.campaignData.deduplicationParameter.options"
                :validation="['required']"
                ref="data_deduplication"
                labels-prefix="campaignDesigner.targetDefinition.deduplication"
                v-if="isCreate || isEdit"
                v-model="deduplicationParameter" />

              <output-field
                :label="$t('campaignDesigner.targetDefinition.deduplication.label')"
                :options="localConfig.campaignData.deduplicationParameter.options"
                labels-prefix="campaignDesigner.targetDefinition.deduplication"
                type="picklist"
                :value="deduplicationParameter"
                v-else />
            </template>
          </div>
        </div>

        <!-- Start date, End date -->
        <div class="row">
          <!-- Start date -->
          <div class="slds-form-element col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.startDate')"
                :validation="[{name: 'is-date-before', param: endDate}, 'is-date-after-today', 'required']"
                ref="data_startDate"
                type="date"
                v-if="isCreate || isEdit"
                v-model="startDate" />

              <output-field
                :label="$t('campaignDesigner.campaignSetup.startDate')"
                :value="startDate"
                type="date"
                v-else />
            </div>
          </div>

          <!-- End date -->
          <div class="slds-form-element slds-size--medium col">
            <div class="slds-form-element slds-size--medium">
              <Input
                :label="$t('campaignDesigner.campaignSetup.endDate')"
                :validation="['is-date-after-today']"
                ref="data_endDate"
                type="date"
                v-if="isCreate || isEdit"
                v-model="endDate" />

              <output-field
                :label="$t('campaignDesigner.campaignSetup.endDate')"
                :value="endDate"
                type="date"
                v-else />
            </div>
          </div>
        </div>

        <!-- Brands, Channels -->
        <div class="row">
          <!-- Brands -->
          <div class="slds-form-element col slds-size--medium" v-if="localConfig.campaignData.campaignBrands.options.length>1">
            <picklist
              :disabled="brandDisabled"
              :label="$t('campaignDesigner.campaignSetup.brands')"
              :options="localConfig.campaignData.campaignBrands.options"
              :validation="brandDisabled ? [] : ['required']"
              labels-prefix="brands"
              multipicklist
              ref="data_brands"
              v-if="isCreate || isEdit"
              v-model="campaignBrands" />

            <output-field
              :label="$t('campaignDesigner.campaignSetup.brands')"
              :options="localConfig.campaignData.campaignBrands.options"
              :value="campaignBrands"
              labels-prefix="brands"
              type="multipicklist"
              v-else />
          </div>          
          <div class="slds-form-element col slds-size--medium" v-else>
            <output-field
              :label="$t('campaignDesigner.campaignSetup.brands')"
              :validation="brandDisabled ? [] : ['required']"
              :options="localConfig.campaignData.campaignBrands.options"
              :value="localConfig.campaignData.campaignBrands.options[0]"
              labels-prefix="brands"
              type="multipicklist"/>
          </div>

          <!-- Channels -->
          <div class="slds-form-element col slds-size--medium">
            <picklist
              :label="$t('campaignDesigner.campaignSetup.channels.label')"
              :options="localConfig.campaignData.campaignChannel.options"
              :validation="['required']"
              labels-prefix="channels"
              multipicklist
              ref="data_channels"
              v-if="isCreate || isEdit"
              v-model="campaignChannel" />

            <output-field
              :label="$t('campaignDesigner.campaignSetup.channels.label')"
              :options="localConfig.campaignData.campaignChannel.options"
              :value="campaignChannel"
              labels-prefix="channels"
              type="multipicklist"
              v-else />
          </div>
        </div>

        <!-- Segment Type, Masterkey -->
        <div class="row">
          <!-- Segment Type -->
          <div class="slds-form-element slds-size--medium col">
            <picklist
              :label="$t('campaignDesigner.campaignSetup.segmentType.label')"
              :options="localConfig.campaignData.segmentType.options"
              labels-prefix="campaignDesigner.campaignSetup.segmentType"
              ref="data_segmentType"
              show-empty-value
              v-if="isCreate || isEdit"
              v-model="segmentType" />

            <output-field
              :label="$t('campaignDesigner.campaignSetup.segmentType.label')"
              :options="localConfig.campaignData.segmentType.options"
              :value="segmentType"
              labels-prefix="campaignDesigner.campaignSetup.segmentType"
              type="picklist"
              v-else />
          </div>

          <!-- Masterkey -->
          <div class="slds-form-element slds-size--medium col" v-if="masterKeys">
            <picklist
              :label="$t('campaignDesigner.campaignSetup.masterKey')"
              :options="masterKeys"
              show-empty-value
              ref="data_masterkey"
              searchable
              v-if="isCreate || isEdit"
              v-model="masterKey" />

            <output-field
              :label="$t('campaignDesigner.campaignSetup.masterKey')"
              :value="masterKey"
              type="custom"
              v-else>
              <span v-if="masterKeys && masterKeys.length && masterKey">{{masterKey.length && masterKeys.find(mk => mk.value === masterKey).label}}</span>
            </output-field>
          </div>
        </div>

        <!-- CCDB Report Enabled, CCDB Report Type -->
        <div class="row">
          <!-- CCDB Report Enabled -->
          <div class="slds-form-element col slds-size--medium">
            <Input
              :label="$t('campaignDesigner.campaignSetup.ccdbReportEnabled')"
              type="checkbox"
              v-if="isCreate || isEdit"
              v-model="ccdbReportEnabled" />

            <output-field
              :label="$t('campaignDesigner.campaignSetup.ccdbReportEnabled')"
              :value="ccdbReportEnabled"
              type="checkbox"
              v-else />
          </div>

          <!-- CCDB Report Type -->
          <!--<div class="slds-form-element slds-size--medium col">
            <picklist
              :label="$t('campaignDesigner.campaignSetup.ccdbReportType.label')"
              :options="localConfig.campaignData.ccdbReportType.options"
              labels-prefix="campaignDesigner.campaignSetup.ccdbReportType"
              show-empty-value
              v-if="isCreate || isEdit"
              v-model="ccdbReportType" />

            <output-field
              :label="$t('campaignDesigner.campaignSetup.ccdbReportType.label')"
              :options="localConfig.campaignData.ccdbReportType.options"
              :value="ccdbReportType"
              labels-prefix="campaignDesigner.campaignSetup.ccdbReportType"
              type="picklist"
              v-else />
          </div>-->
        </div>

        <!-- Finality, Manager -->
        <div class="row">
          <!-- Finality -->
          <div class="slds-form-element col slds-size--medium">
            <picklist
              :label="$t('campaignDesigner.campaignSetup.finality.label')"
              :options="localConfig.campaignData.finality.options"
              :tooltip="$t('campaignDesigner.campaignSetup.finality.tooltip.sales')"
              tooltip-direction="right"
              labels-prefix="campaignDesigner.campaignSetup.finality"
              ref="data_finality"
              show-empty-value
              v-if="isCreate || isEdit"
              v-model="finality" />

            <output-field
              :label="$t('campaignDesigner.campaignSetup.finality.label')"
              :options="localConfig.campaignData.finality.options"
              :value="finality"
              labels-prefix="campaignDesigner.campaignSetup.finality"
              type="picklist"
              v-else />
          </div>

          <!-- Manager -->
          <div class="slds-form-element slds-size--medium col" v-if="localConfig.campaignData.manager.options.length>1">
            <picklist
              :label="$t('campaignDesigner.campaignSetup.manager.label')"
              :options="localConfig.campaignData.manager.options"
              labels-prefix="campaignDesigner.campaignSetup.manager"
              show-empty-value
              v-if="isCreate || isEdit"
              v-model="manager" />

            <output-field
              :label="$t('campaignDesigner.campaignSetup.manager.label')"
              :options="localConfig.campaignData.manager.options"
              :value="manager"
              labels-prefix="campaignDesigner.campaignSetup.manager"
              type="picklist"
              v-else />
          </div>

          <!-- Manager only one-->
          <div class="slds-form-element slds-size--medium col" v-else-if="localConfig.campaignData.manager.options.length==1">
            <output-field
              :label="$t('campaignDesigner.campaignSetup.manager.label')"
              :options="localConfig.campaignData.manager.options"
              :value="localConfig.campaignData.manager.options[0]"
              labels-prefix="campaignDesigner.campaignSetup.manager"
              type="picklist"/>
          </div>
        </div>

        <!-- Description -->
        <div class="row">
          <!-- Description -->
          <div class="slds-form-element col slds-size--medium">
            <div class="slds-form-element slds-size--medium">
              <text-area
                :label="$t('campaignDesigner.campaignSetup.description')"
                v-if="isCreate || isEdit"
                v-model="description" />

              <output-field
                :label="$t('campaignDesigner.campaignSetup.description')"
                :value="description"
                type="text"
                v-else />
            </div>
          </div>
        </div>
      </card>


    </div>
  </div>
</template>
<script>
import _ from 'lodash'
import moment from 'moment'
import { validate } from '../../utils/utils'
import CampaignTypeSelector from '../cc/campaign-setup/CampaignTypeSelector'
import Card from '../../slds/Card'
import HasMode from '../../utils/HasMode'
import Input from '../../slds/Input'
import OutputField from '../../slds/OutputField'
import Picklist from '../../slds/Picklist'
import Spinner from '../../Spinner'
import TextArea from '../../slds/TextArea'
export default {
  mixins: [HasMode],
  components: {
    CampaignTypeSelector,
    Card,
    Input,
    OutputField,
    Picklist,
    Spinner,
    TextArea
  },
  props: {
    campaignStatus: String,
    loading: Boolean,
    mode: String
  },
  data () {
    return {
      deduplicationParameter: '',
      brandDisabled: false,
      campaignBrands: [],
      campaignChannel: [],
      campaignType: {},
      description: '',
      endDate: '',
      finality: '',
      localConfig: {},
      manager: '',
      markets: '',
      masterKey: '',
      masterKeys: undefined,
      name: '',
      oldCampaignChannel: [],
      onlyHeader: false,
      recordType: '',
      segmentType: '',
      startDate: '',
      targetSource: ''
    }
  },
  beforeCreate () {
    this.$emit('startLoading')
  },
  created () {
    this.callLoadFromStore()
    this.$emit('endLoading')
  },
  beforeDestroy () {
    this.campaignBrands = undefined
    this.campaignChannel = undefined
    this.campaignType = undefined
    this.description = undefined
    this.endDate = undefined
    this.finality = undefined
    this.localConfig = undefined
    this.manager = undefined
    this.markets = undefined
    this.masterKey = undefined
    this.masterKeys = undefined
    this.name = undefined
    this.oldCampaignChannel = undefined
    this.onlyHeader = undefined
    this.recordType = undefined
    this.segmentType = undefined
    this.startDate = undefined
    this.targetSource = undefined
  },
  methods: {
    async callLoadFromStore () {
      const campaign = this.loadFromStore('campaignData', ['loading', 'oldConfig'])

      if (this.$route.query.type) {
        this.recordType = this.$route.query.type
      }

      this.localConfig = this.$store.getters['campaignDesigner/state'].currentCampaignConfig

      if (this.localConfig.campaignData.type.options.length === 1) {
        const selectedType = this.localConfig.campaignData.type.options[0]
        this.campaignType = {
          label: selectedType.name,
          value: selectedType.code,
          color: selectedType.color,
          disableOrchestrationFlagEditable: selectedType.disableOrchestrationFlagEditable,
          disableOrchestrationFlagDefault: selectedType.disableOrchestrationFlagDefault
        }
        this.disableOrchestration = selectedType.disableOrchestrationFlagDefault
      }

      if (campaign.config.campaignData && campaign.config.campaignData.type && campaign.config.campaignData.type.selected) {
        const selectedType = campaign.config.campaignData.type.selected
        this.campaignType = {
          label: selectedType.name,
          value: selectedType.code,
          color: selectedType.color,
          disableOrchestrationFlagEditable: selectedType.disableOrchestrationFlagEditable,
          disableOrchestrationFlagDefault: selectedType.disableOrchestrationFlagDefault
        }
      }
      this.masterKeys = (await this.$store.dispatch('settings/getSettings')).masterKeys
    },
    validate () {
      if (this.isCreate || this.isEdit) {
        return validate(this, 'data', true)
      } else {
        const dateFrom = moment(this.startDate, 'YYYY-MM-DD')
        if (this.campaignStatus === 'Draft' && dateFrom.isBefore(moment(), 'day')) {
          this.showToast('error', this.$t('validation.invalidData'), 'text', this.$t('campaignDesigner.campaignSetup.validation.startDateMustBeInFuture'))
        } else {
          return true
        }
      }
    },
    getSectionToSave () {
      const campaignType = {
        options: this.localConfig.campaignData.type.options,
        selected: this.localConfig.campaignData.type.options.find(type => type.code === this.campaignType.value)
      }

      let endDate = ''

      if (moment(this.endDate).isValid()) {
        endDate = moment(this.endDate).format('YYYY-MM-DD')
      }

      return {
        flat: {
          brands: this.campaignBrands,
          ccdbReportEnabled: this.ccdbReportEnabled,
          ccdbReportType: this.ccdbReportType,
          description: this.description,
          finality: this.finality,
          marketCodes: this.markets,
          endDate,
          manager: this.manager,
          masterKey: this.masterKey,
          name: this.name,
          onlyHeader: this.onlyHeader,
          recordType: this.recordType,
          segmentType: this.segmentType,
          type: this.campaignType.value,
          startDate: moment(this.startDate).format('YYYY-MM-DD')
        },
        config: {
          campaignBrands: {
            options: this.localConfig.campaignData.campaignBrands.options,
            selected: this.campaignBrands
          },
          campaignChannel: {
            options: this.localConfig.campaignData.campaignChannel.options,
            selected: this.campaignChannel
          },
          ccdbReportType: {
            options: this.localConfig.campaignData.ccdbReportType.options,
            selected: this.ccdbReportType
          },
          deduplicationParameter: {
            options: this.localConfig.campaignData.deduplicationParameter.options,
            selected: this.deduplicationParameter
          },
          description: this.description,
          finality: {
            options: this.localConfig.campaignData.finality.options,
            selected: this.finality
          },
          manager: {
            options: this.localConfig.campaignData.manager.options,
            selected: this.manager
          },
          markets: {
            options: this.localConfig.campaignData.markets.options,
            selected: this.markets
          },
          masterKey: {
            selected: this.masterKey
          },
          segmentType: {
            options: this.localConfig.campaignData.segmentType.options,
            selected: this.segmentType
          },
          targetSource: {
            options: this.localConfig.campaignData.targetSource.options,
            selected: this.targetSource
          },
          type: campaignType
        }
      }
    }
  },
  computed: {
    nbsp () {
      return '\xa0'
    }
  },
  watch: {
    campaignChannel (newValue) {
      this.oldCampaignChannel = _.cloneDeep(newValue)
    },
    endDate () {
      if (this.isCreate || this.isEdit) {
        this.$refs.data_startDate.clearValidationError()
      }
    },
    masterKey (value) {
      if (value === '') {
        this.masterKey = null
      }
    },
    targetSource (to) {
      if (to === 'contacts') {
        this.brandDisabled = true
        this.deduplicationParameter = 'dedup_by_contact'
      } else {
        this.brandDisabled = false
        this.deduplicationParameter = ''
      }
    }
  },

}
</script>
<style lang="scss" scoped>
  .row {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: flex-start;
  }

  .col {
    flex-grow: 1;
  }

  .type-color-container {
    margin-right: 10px;
  }

  .full-height {
    height: 100%;
    overflow-y: auto;
  }

  Input {
    width: unset !important;
  }
</style>
